import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styles from './PictureDayNotice.module.scss';
import { routes } from '../../routes';

// WHY THIS EXISTS
//
// The school-photography industry trains parents to pre-order, so a steady
// stream of them reach the access-code form weeks before their child has been
// photographed. We don't sell pre-orders -- photos are taken on picture day and
// families are then emailed and texted a link -- so those parents type
// something that cannot possibly match and hit the generic "we couldn't find a
// match" wall with no idea why. This tells them, at the moment they pick their
// school, what is actually going to happen and when.
//
// WRITTEN FOR A PHONE, AND FOR SOMEONE WHO IS NOT READING CAREFULLY. Most
// parents see this on mobile, mid-something-else. So it is built around one
// glanceable fact -- the date -- and one tap target, rather than a paragraph.
// An earlier version set the heading, the explanation, the dates and the
// actions at nearly the same size and weight; everything competed, so nothing
// was read. The rule here: ONE hero (the date), one button, nothing else.

const STRINGS = {
  en: {
    // Says what WILL happen, not what hasn't. "Photos aren't ready yet" lands
    // as a refusal on a parent who came to spend money; this frames the same
    // fact as the ordinary sequence of events, which is what it actually is.
    //
    // "ON Picture Day", not "after" it, is the accurate promise: the
    // photos-are-ready notice goes out within ParentReady::Settings::DEADLINE
    // (30 minutes) of a student being shot, so the pictures really are
    // orderable that same day. "After picture day" understated it and invited
    // a parent to stop checking.
    preSeasonTitle: 'Pictures will be available to order on Picture Day.',
    // The button carries the promise rather than a sentence above it. A line
    // reading "we'll email and text you a link" told the parent there was
    // nothing to do -- true only if we already have their details, which for
    // someone who found this page on their own we may well not.
    // Two lines: the action, then what it's for. One long line wrapped
    // unpredictably at phone widths -- sometimes two lines, sometimes three --
    // and a button whose height moves with the viewport reads as unfinished.
    // Splitting it makes the wrap deliberate and puts the verb on its own line.
    notifyCtaAction: 'Notify me',
    notifyCtaWhen: 'when photos are ready',
    // THE ANTI-DEAD-END LINE. Everything else on this screen is about a school
    // that hasn't been photographed, so a parent holding a code from an earlier
    // shoot can easily read the whole page as "not for you" and leave. Saying
    // outright that old codes STILL WORK is the point of this block -- an
    // earlier version only asked "Student already photographed?", which is a
    // question they might answer "no" to (their child wasn't photographed
    // *this* year) and then skip past the one thing that would have helped.
    //
    // "past picture days and school years" rather than "past years": at a
    // school whose SPRING shoot is still ahead, a code from that same school
    // year's fall pictures is valid, so "past year" would be wrong.
    codeSwitchTitle: 'Already have an access code?',
    codeSwitchBody: 'Codes from past picture days and school years still work.',
  },
  es: {
    preSeasonTitle: 'Las fotos estarán disponibles para ordenar el día de fotos.',
    // NOTE: /c/register is English-only today, so this hands a Spanish reader
    // an English form. Better than not offering it at all -- but if that page
    // gains a `lang` param, pass currentLang through here.
    notifyCtaAction: 'Avísenme',
    notifyCtaWhen: 'cuando las fotos estén listas',
    codeSwitchTitle: '¿Ya tiene un código de acceso?',
    codeSwitchBody: 'Los códigos de días de fotos y años escolares anteriores siguen funcionando.',
  },
};

// Format 'YYYY-MM-DD' as a long local date in the reader's language.
//
// The split/Number dance is NOT incidental. `new Date('2026-09-22')` parses an
// ISO date-only string as UTC MIDNIGHT, which in every US timezone renders as
// September 21 -- so the page would advertise picture day one day early, for
// everyone. The server deliberately sends a NAMED CALENDAR DAY rather than an
// instant (see SchoolPictureDays), and the three-argument Date constructor is
// what reads it back as one: it builds the date in local time, no shifting.
export function formatDay(iso, lang) {
  const [year, month, day] = String(iso).split('-').map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat(lang === 'es' ? 'es' : 'en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

export default function PictureDayNotice({ pictureDays, lang }) {
  const t = STRINGS[lang === 'es' ? 'es' : 'en'];
  const days = pictureDays?.days || [];

  // PICTURE DAY HAS HAPPENED -- say nothing at all, and let the parent get on
  // with typing their code.
  //
  // `seasonStarted` flips on the morning of the school's FIRST portrait day.
  // From that moment this component has nothing left to explain: ordering is
  // either open or hours away, and the retake dates it used to list are for
  // families who have already been photographed and want another go, which is
  // almost nobody reading this page.
  //
  // Deliberately keyed to the DATE rather than to whether the photos are
  // actually on sale yet. Gating on the images would leave the card up during
  // the gap between the shoot and the photos going live -- real, since a
  // student's photos-ready notice can take up to ParentReady::Settings::DEADLINE
  // -- and every parent at the school would see retake dates in the meantime.
  if (pictureDays?.seasonStarted) return null;

  // Nothing scheduled means nothing honest to say -- render nothing rather
  // than an empty card. Covers the school whose season is over as well as the
  // one whose next year isn't on the calendar yet.
  if (days.length === 0) return null;

  const formatted = (list) => list.map((day) => formatDay(day.date, lang)).filter(Boolean);

  // ONE DATE ONLY: the picture day. A retake day is for a family who has
  // already been photographed and wants another go -- irrelevant to someone
  // whose child hasn't sat yet, and a second date next to the first invites
  // them to wonder which one is theirs.
  //
  // Falls back to the retakes when a school somehow has no picture day left on
  // the calendar (its shoot was declined or removed) so the card can't render
  // a heading over nothing -- a retake day is a shoot for anyone not yet
  // photographed.
  const shootDates = formatted(days.filter((day) => day.kind !== 'retake_day'));
  const heroDates = shootDates.length > 0
    ? shootDates
    : formatted(days.filter((day) => day.kind === 'retake_day'));

  return (
    <>
      {/* role=status: this is the page's explanation for why ordering isn't
          possible yet, so a screen reader should hear it when it appears. */}
      <div className={styles.noticePre} role="status">
        <div className={styles.head}>
          <span className={styles.icon} aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </span>
          <p className={styles.title}>{t.preSeasonTitle}</p>
        </div>

        {/* THE ONE THING A PARENT CAME FOR, and now the only thing on this half
          of the card. Set large and dark so it survives a glance at arm's
          length on a phone.

          No "Picture Day" label above it -- the heading a line up already ends
          in those two words, so the label was the same phrase twice in a row
          and the eye had to step over it to reach the date. */}
        {heroDates.length > 0 && (
        <div className={styles.hero}>
          {heroDates.map((date) => (
            <div key={date} className={styles.heroDate}>{date}</div>
          ))}
        </div>
        )}

        {/* Two lines: the action, with the bell on the same line as the verb,
            then what it is for underneath. One long label wrapped
            unpredictably across phone widths -- sometimes two lines, sometimes
            three, with the bell orphaned above the text -- and a button whose
            height moves with the viewport reads as unfinished. Splitting it
            makes the wrap deliberate. */}
        <Link className={styles.ctaPrimary} to={routes.registration()}>
          <span className={styles.ctaLineAction}>
            {/* A bell reads as "alert me" across both channels this covers.
                Decorative -- the label already says it, so it is hidden rather
                than announced ahead of the words. Sized to the cap height of
                the word beside it so the two sit on one baseline. */}
            <span className={styles.ctaIcon} aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </span>
            {t.notifyCtaAction}
          </span>
          <span className={styles.ctaLineWhen}>{t.notifyCtaWhen}</span>
        </Link>
      </div>

      {/* HEADS THE ACCESS-CODE FIELDS, which sit directly below it.

          Those fields used to be hidden behind a button here, and the button
          was the dead end in miniature: a parent holding a code from an earlier
          shoot had to recognise themselves in a one-line prompt and take an
          extra step before the page would even show them a box to type in.
          Now the fields are always there and this just tells them the code
          they're holding is still good.

          OUTSIDE the blue card on purpose. Everything inside that card is about
          a school that has NOT been photographed yet; this is for the parent
          whose child HAS been, so putting it in there filed the exception under
          the rule it contradicts. */}
      <div className={styles.codeSwitch}>
        <p className={styles.codeSwitchTitle}>{t.codeSwitchTitle}</p>
        <p className={styles.codeSwitchBody}>{t.codeSwitchBody}</p>
      </div>
    </>
  );
}

PictureDayNotice.defaultProps = {
  // null covers all three "nothing to say" cases -- not loaded yet, nothing
  // scheduled, and the lookup failed -- and every one of them renders nothing.
  pictureDays: null,
  lang: 'en',
};

PictureDayNotice.propTypes = {
  pictureDays: PropTypes.shape({
    seasonStarted: PropTypes.bool,
    days: PropTypes.arrayOf(PropTypes.shape({
      date: PropTypes.string,
      kind: PropTypes.string,
    })),
  }),
  lang: PropTypes.string,
};
